/**
 * Base API client for making authenticated requests to META backend.
 * Handles Authorization header injection, error responses, and retry logic.
 */

import { META_AUTH_API_URL } from '@mental-mint/shared-types';
import { authManager } from './authManager';
import { logger } from '../Analytics/Logger';

const API_BASE_URL = META_AUTH_API_URL;

export class ApiError extends Error {
  constructor(
    public statusCode: number,
    public message: string,
    public responseBody?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message: string = 'Session expired. Please log in again.') {
    super(401, message);
    this.name = 'UnauthorizedError';
  }
}

export class ForbiddenError extends ApiError {
  constructor(message: string = 'Access denied.') {
    super(403, message);
    this.name = 'ForbiddenError';
  }
}

export class ConflictError extends ApiError {
  constructor(
    message: string = 'Data conflict detected.',
    public cloudTimestamp?: number
  ) {
    super(409, message);
    this.name = 'ConflictError';
  }
}

/**
 * Timeout for API requests (ms)
 * Prevents indefinite blocking for users with slow/offline connections
 */
const API_TIMEOUT_MS = 8000; // 8 seconds

/**
 * Fetch with timeout support
 * @param url - Request URL
 * @param options - Fetch options
 * @param timeoutMs - Timeout in milliseconds
 * @returns Response or timeout error
 */
async function fetchWithTimeout(url: string, options: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const startTime = Date.now();
  
  const timeoutId = setTimeout(() => {
    logger.warn(`[apiClient] Request timeout after ${timeoutMs}ms for ${url}`);
    controller.abort();
  }, timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error: any) {
    clearTimeout(timeoutId);
    const elapsed = Date.now() - startTime;
    if (error.name === 'AbortError') {
      const msg = `Request timed out after ${elapsed}ms (limit: ${timeoutMs}ms). Please check your connection and try again.`;
      logger.error('[apiClient] Timeout error:', msg);
      throw new ApiError(408, msg);
    }
    logger.error('[apiClient] Fetch error:', error.message || error);
    throw error;
  }
}

/**
 * Make an authenticated API request to META backend.
 * Automatically includes Authorization header if session token exists.
 * 
 * @param endpoint - API endpoint path (e.g., '/auth/send-magic-link')
 * @param options - Fetch options (method, body, headers)
 * @param timeoutMs - Optional timeout override (default: 8000ms)
 * @returns Parsed JSON response
 * @throws ApiError on HTTP error responses or timeout
 */
export async function apiClient<T = any>(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs: number = API_TIMEOUT_MS
): Promise<T> {
  // Build full URL
  const url = `${API_BASE_URL}${endpoint}`;

  // Prepare headers
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers || {})
  };

  // Add Authorization header if authenticated
  const sessionToken = authManager.getSessionToken();
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  }

  // Make request with timeout
  const response = await fetchWithTimeout(url, {
    ...options,
    headers
  }, timeoutMs);

  // Handle error responses
  if (!response.ok) {
    const responseBody = await response.json().catch(() => ({}));
    
    // Get user-friendly error message
    let errorMessage = responseBody.error || responseBody.message;
    
    // If no specific error message, provide user-friendly fallback based on status code
    if (!errorMessage) {
      switch (response.status) {
        case 400:
          errorMessage = 'Invalid request. Please check your input and try again.';
          break;
        case 401:
          errorMessage = 'Session expired. Please log in again.';
          break;
        case 403:
          errorMessage = 'Access denied.';
          break;
        case 404:
          errorMessage = 'Service not found. Please try again later.';
          break;
        case 409:
          errorMessage = 'Data conflict detected.';
          break;
        case 500:
        case 502:
        case 503:
        case 504:
          errorMessage = 'Server error. Please try again later.';
          break;
        default:
          errorMessage = 'Something went wrong. Please try again later.';
      }
    }

    // Handle specific error codes
    if (response.status === 401) {
      // Clear invalid session
      authManager.clearSession();
      throw new UnauthorizedError(errorMessage);
    }

    if (response.status === 403) {
      throw new ForbiddenError(errorMessage);
    }

    if (response.status === 409) {
      // Conflict error (stale data from saveGameState)
      throw new ConflictError(errorMessage, responseBody.cloudTimestamp);
    }

    throw new ApiError(response.status, errorMessage, responseBody);
  }

  // Parse and return JSON response
  return await response.json();
}
